// src/redux/selectors/profile/userSelectors.ts

import { RootState } from "../../storeTypes";
import { MemberProfileType } from "../../../types/profiles/memberProfileTypes";
import { GuestProfileType } from "../../../types/profiles/guestProfileTypes";

// ----------------------- AUTH SELECTORS -----------------------

export const selectAuthUser = (state: RootState) => state.auth.user;
export const selectAuthLoadingStatus = (state: RootState) => state.auth.loading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectAuthSuccessMessage = (state: RootState) => state.auth.successMessage;
export const selectAuthToken = (state: RootState) => state.auth.token;
export const selectIsLoggedIn = (state: RootState) => state.auth.isLoggedIn;
export const selectHasCompletedSignup = (state: RootState) => state.auth.hasCompletedSignup;

export const selectNeedsLanguageOnboarding = (state: RootState) =>
  state.auth.isLoggedIn &&
  state.auth.user?.language_onboarding_completed === false;

export const selectLanguageLoading = (state: RootState) =>
  state.auth.languageLoading;

export const selectProfileLanguageOptions = (state: RootState) =>
  state.auth.languageOptions;

export const selectLanguageOnboardingCompleted = (state: RootState) =>
  state.auth.user?.language_onboarding_completed === true;

export const selectIsAccountDeletionPending = (
  state: RootState,
) =>
  state.auth.user?.is_deleted === true &&
  !state.auth.user?.deletion_completed_at;

export const selectAccountDeletionScheduledFor = (
  state: RootState,
) =>
  state.auth.user?.deletion_scheduled_for ?? null;
  
// ----------------------- PROFILE SELECTORS -----------------------

export const selectProfileCurrentUser = (state: RootState) => state.profile.profile;
export const selectProfileLoadingStatus = (state: RootState) => state.profile.loading;
export const selectProfileError = (state: RootState) => state.profile.error;
export const selectProfileSuccessMessage = (state: RootState) => state.profile.successMessage;

// Narrowed selectors ------------------------------------------------------------

export const selectMemberProfile = (
  state: RootState
): MemberProfileType | null => {
  const profile = state.profile.profile;
  if (profile && "denomination_branch" in profile) {
    return profile as MemberProfileType;
  }
  return null;
};

export const selectGuestProfile = (
  state: RootState
): GuestProfileType | null => {
  const profile = state.profile.profile;
  if (profile && !("denomination_branch" in profile)) {
    return profile as GuestProfileType;
  }
  return null;
};

export const selectIsMemberProfileLoaded = (state: RootState) =>
  !!selectMemberProfile(state);

export const selectIsGuestProfileLoaded = (state: RootState) =>
  !!selectGuestProfile(state);

// Bootstrap selectors -----------------------------------------------------------

export const selectProfileBootstrapData = (state: RootState) =>
  state.profileBootstrap.data;

export const selectProfileBootstrapLoading = (state: RootState) =>
  state.profileBootstrap.loading;

export const selectProfileBootstrapError = (state: RootState) =>
  state.profileBootstrap.error;

export const selectActiveProfileType = (state: RootState) =>
  state.profileBootstrap.data?.profile_type ?? null;

export const selectIsActiveMember = (state: RootState) =>
  state.profileBootstrap.data?.profile_type === "member";

export const selectIsActiveGuest = (state: RootState) =>
  state.profileBootstrap.data?.profile_type === "guest";